
import React, { useState, useCallback } from 'react';
const searchCompanyInfo = async (query: string): Promise<CompanyInfo[]> => {
  if (!query.trim()) return [];
  try {
    const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`);
    if (!response.ok) throw new Error(`伺服器錯誤: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Error:", error);
    throw new Error("搜尋公司資訊時發生錯誤。");
  }
};
import type { CompanyInfo } from './types';
import { SearchBar } from './components/SearchBar';
import { ResultsDisplay } from './components/ResultsDisplay';
import { LoadingSpinner } from './components/LoadingSpinner';
import { LogoIcon } from './components/icons/LogoIcon';

const App: React.FC = () => {
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<CompanyInfo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const handleSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setError('請輸入公司名稱進行搜尋。');
      return;
    }
    setIsLoading(true);
    setError(null);
    setResults([]);
    setHasSearched(true);
    setQuery(searchQuery);

    try {
      const data = await searchCompanyInfo(searchQuery);
      setResults(data);
    } catch (err) {
      console.error(err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('發生未知錯誤，請稍後再試。');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center p-8">
          <LoadingSpinner />
          <p className="text-slate-500 dark:text-slate-400 mt-4">正在從網路搜尋最新的公司資訊...</p>
        </div>
      );
    }

    if (error) {
      return <div className="text-center p-8 text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg">{error}</div>;
    }

    if (!hasSearched) {
      return (
        <div className="text-center p-8 text-slate-500 dark:text-slate-400">
          <p>輸入公司部分名稱，快速查找統一編號、地址等資訊。</p>
        </div>
      );
    }

    if (results.length > 0) {
      return <ResultsDisplay results={results} />;
    }

    if (hasSearched && results.length === 0) {
      return (
        <div className="text-center p-8 text-slate-500 dark:text-slate-400">
          <p>找不到符合「{query}」的公司資訊，請嘗試其他關鍵字。</p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-sans p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <LogoIcon className="h-10 w-10 text-primary-600" />
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">
              公司資訊快速查找
            </h1>
          </div>
          <p className="text-slate-600 dark:text-slate-400">
            輸入公司部分名稱，即時取得統一編號、註冊地址與資本額。
          </p>
        </header>

        <main>
          <div className="sticky top-4 z-10 bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur-sm p-4 rounded-xl shadow-lg dark:shadow-2xl mb-8 border border-slate-200 dark:border-slate-700">
            <SearchBar onSearch={handleSearch} isLoading={isLoading} />
          </div>

          <div className="transition-all duration-300">
            {renderContent()}
          </div>
        </main>

        <footer className="text-center mt-12 text-sm text-slate-500 dark:text-slate-400 space-y-1">
          <p>資料來源於網路搜尋，僅供參考，請以官方登記資訊為準。</p>
          <p>開發者：Louis11 Tsai ·
            <a href="mailto:zzz38123@gmail.com" className="hover:underline mx-1">zzz38123@gmail.com</a> ·
            <a href="https://evoleth.yisuns.com?from=company-lookup" target="_blank" rel="noopener noreferrer" className="hover:underline">evoleth.yisuns.com</a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default App;
